import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DeletionWarning from '../components/DeletionWarning';
import { 
  Home, 
  FileText, 
  BarChart2, 
  Package, 
  User, 
  CreditCard, 
  LogOut, 
  Menu, 
  X,
  QrCode,
  Bell
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, profile, signOut } = useAuth();
  const { isOnTrial, daysLeftInTrial, isSubscribed, isTrialExpired } = useSubscription();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Note: Removed automatic redirect to billing page to allow users to access dashboard

  const navItems = [
    { to: "/dashboard", icon: <Home size={20} />, label: "Dashboard" },
    { to: "/dashboard/forms", icon: <FileText size={20} />, label: "Forms" },
    { to: "/dashboard/responses", icon: <BarChart2 size={20} />, label: "Responses" },
    { to: "/dashboard/order-stickers", icon: <Package size={20} />, label: "Design & Order" },
    { to: "/dashboard/account", icon: <User size={20} />, label: "Account" },
    { to: "/dashboard/billing", icon: <CreditCard size={20} />, label: "Billing" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top navigation */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <div className="flex items-center">
                <QrCode size={28} className="text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center">
            {/* Notifications */}
            <button className="p-2 rounded-full text-gray-400 hover:text-gray-500 relative">
              <Bell size={20} />
              <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500"></span>
            </button>
            
            {/* User dropdown */}
            <div className="ml-4 relative flex items-center">
              <div className="flex items-center">
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-700">
                    {profile?.company_name || 'Your Account'}
                  </div>
                  <button 
                    onClick={signOut}
                    className="text-xs text-gray-500 flex items-center"
                  >
                    <LogOut size={12} className="mr-1" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar for desktop */}
        <aside className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64 border-r border-gray-200 bg-white">
            <div className="h-0 flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              {/* Subscription status */}
              {isOnTrial && !isSubscribed && (
                <div className="px-4 py-3 mx-4 mb-6 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700 font-medium">
                    Trial: {daysLeftInTrial} days remaining
                  </p>
                  <NavLink 
                    to="/dashboard/billing"
                    className="mt-1 inline-block text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Upgrade now →
                  </NavLink>
                </div>
              )}

              {/* Navigation */}
              <nav className="mt-5 px-2 space-y-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    className={({ isActive }) =>
                      `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                  >
                    <div className="mr-3">{item.icon}</div>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </aside>

        {/* Mobile sidebar */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 z-40 flex">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            ></div>

            {/* Sidebar */}
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>
              
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <QrCode size={28} className="text-blue-600" />
                  <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
                </div>
                
                {/* Mobile subscription status */}
                {isOnTrial && !isSubscribed && (
                  <div className="px-4 py-3 mx-4 my-6 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700 font-medium">
                      Trial: {daysLeftInTrial} days remaining
                    </p>
                    <NavLink 
                      to="/dashboard/billing"
                      className="mt-1 inline-block text-xs text-blue-600 hover:text-blue-800 font-medium"
                      onClick={() => setSidebarOpen(false)}
                    >
                      Upgrade now →
                    </NavLink>
                  </div>
                )}

                <nav className="mt-5 px-2 space-y-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      className={({ isActive }) =>
                        `group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                          isActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <div className="mr-4">{item.icon}</div>
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <DeletionWarning />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;